import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from './common.service';
import { SEOConfigurationService } from './seo-configuration.service';

// Ported from customer-website's MetaTagsService (minus its Jazeera/Revest
// dual-brand split, which doesn't apply to this single-brand supplier app):
// tab title, meta description and OG tags all come from SEOConfigurationService
// at runtime, and stay in sync when the language changes.
@Injectable({
	providedIn: 'root',
})
export class MetaTagsService {
	private lastPageTitle?: string;
	private lastDescription?: string;

	constructor(
		private meta: Meta,
		private title: Title,
		private translate: TranslateService,
		private commonService: CommonService,
		private seoConfigurationService: SEOConfigurationService,
	) {
		this.translate.onLangChange.subscribe((event) => {
			const lang = this.normalizeLang(event?.lang);
			this.applyBaseMeta(lang);
			if (this.lastPageTitle || this.lastDescription) {
				this.applyPageMeta(lang, this.lastPageTitle, this.lastDescription);
			}
		});
	}

	private normalizeLang(lang: string | null | undefined): 'en' | 'ar' {
		return lang === 'ar' ? 'ar' : 'en';
	}

	private currentLang(): 'en' | 'ar' {
		if (this.translate.currentLang) {
			return this.normalizeLang(this.translate.currentLang);
		}
		return this.commonService.domDirectionLTR ? 'en' : 'ar';
	}

	initializeMetaTags(): void {
		this.applyBaseMeta(this.currentLang());
	}

	private baseTitle(lang: 'en' | 'ar'): string {
		const tabTitle = lang === 'en'
			? this.seoConfigurationService.get('tab_title')
			: this.seoConfigurationService.get('tab_title_ar');
		return tabTitle + ' Supplier' || 'Revest Supplier';
	}

	private applyBaseMeta(lang: 'en' | 'ar') {
		this.title.setTitle(this.baseTitle(lang));

		const description = this.seoConfigurationService.get('meta_description');
		if (description) {
			this.meta.updateTag({ name: 'description', content: description });
			this.meta.updateTag({ property: 'og:description', content: description });
		}

		const brandName = lang === 'en'
			? this.seoConfigurationService.get('brand_name')
			: this.seoConfigurationService.get('brand_name_ar');
		this.meta.updateTag({ property: 'og:title', content: brandName || this.baseTitle(lang) });
	}

	updatePageMetaTags(title: string, description?: string): void {
		this.lastPageTitle = title;
		this.lastDescription = description;
		this.applyPageMeta(this.currentLang(), title, description);
	}

	private applyPageMeta(lang: 'en' | 'ar', title?: string, description?: string) {
		const base = this.baseTitle(lang);

		if (title) {
			const fullTitle = `${base} | ${title}`;
			this.title.setTitle(fullTitle);
			this.meta.updateTag({ property: 'og:title', content: fullTitle });
		} else {
			this.title.setTitle(base);
			this.meta.updateTag({ property: 'og:title', content: base });
		}

		if (description) {
			this.meta.updateTag({ name: 'description', content: description });
			this.meta.updateTag({ property: 'og:description', content: description });
		}
	}
}
